import { useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '@/components/common';
import {
  describeBulkResult,
  isCompleteSuccess,
  runBulkAction,
  type BulkOperation,
  type BulkResult,
} from '@/services/bulkActions';

/**
 * The strip that appears once rows are ticked: "6 selected", then the actions.
 *
 * Two things it refuses to do, both deliberate:
 *
 * 1. **It never reports a partial failure as success.** The server answers per
 *    id; when some rows were refused, the refusals are shown and the selection
 *    for those rows is KEPT so the person can see and retry exactly what did
 *    not happen. Clearing everything and saying "done" is the failure mode this
 *    platform repeats most often.
 * 2. **Delete asks first**, and the confirmation names the count. Delete here is
 *    a soft delete that is refused while a record is still referenced, so it is
 *    recoverable -- but a mis-click on forty rows is still worth one question.
 */
interface BulkActionBarProps {
  /** List path without slashes, e.g. `geography/states`. */
  resource: string;
  selectedIds: string[];
  /** Plural noun for the messages, e.g. "states". */
  entityName: string;
  /** Called after any action that changed something, to refetch the list. */
  onChanged: () => void;
  /** Called to drop the selection once everything succeeded. */
  onClear: () => void;
  /** Hide Activate/Deactivate for a resource with no Active flag. */
  allowStatusChange?: boolean;
  allowDelete?: boolean;
  className?: string;
}

export function BulkActionBar({
  resource,
  selectedIds,
  entityName,
  onChanged,
  onClear,
  allowStatusChange = true,
  allowDelete = true,
  className,
}: BulkActionBarProps) {
  const [busy, setBusy] = useState<BulkOperation | null>(null);
  const [lastResult, setLastResult] = useState<BulkResult | null>(null);

  if (selectedIds.length === 0) return null;

  const run = async (action: BulkOperation) => {
    if (action === 'delete') {
      const ok = window.confirm(
        `Delete ${selectedIds.length} ${selectedIds.length === 1 ? entityName.replace(/s$/, '') : entityName}? ` +
          'Records still in use will be refused and listed.'
      );
      if (!ok) return;
    }

    setBusy(action);
    setLastResult(null);
    try {
      const result = await runBulkAction(resource, selectedIds, action);
      setLastResult(result);
      const message = describeBulkResult(result);
      if (isCompleteSuccess(result)) {
        toast.success(message);
        onClear();
      } else {
        // Not an error toast when some of it worked — it did work, partly, and
        // saying otherwise would be as wrong as claiming full success.
        toast(message, { icon: result.succeeded.length ? '⚠️' : '❌' });
      }
      if (result.succeeded.length) onChanged();
    } catch (error) {
      const detail =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        'The action could not be completed.';
      toast.error(detail);
      // Deliberately no onClear() and no onChanged(): nothing is known to have
      // changed, and dropping the selection would hide what was attempted.
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className={
        className ??
        'flex flex-col gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 dark:border-primary-800 dark:bg-primary-900/40'
      }
      role="region"
      aria-label="Bulk actions"
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {selectedIds.length} selected
        </span>
        <div className="flex flex-wrap gap-2">
          {allowStatusChange && (
            <>
              <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => run('activate')}>
                {busy === 'activate' ? 'Activating…' : 'Activate'}
              </Button>
              <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => run('deactivate')}>
                {busy === 'deactivate' ? 'Deactivating…' : 'Deactivate'}
              </Button>
            </>
          )}
          {allowDelete && (
            <Button size="sm" variant="danger" disabled={busy !== null} onClick={() => run('delete')}>
              {busy === 'delete' ? 'Deleting…' : 'Delete'}
            </Button>
          )}
          <Button size="sm" variant="ghost" disabled={busy !== null} onClick={onClear}>
            Clear
          </Button>
        </div>
      </div>

      {lastResult && lastResult.failed.length > 0 && (
        <ul className="mt-1 space-y-1 text-sm text-amber-800 dark:text-amber-300">
          {lastResult.failed.slice(0, 5).map((failure) => (
            <li key={failure.id}>{failure.detail}</li>
          ))}
          {lastResult.failed.length > 5 && (
            <li>…and {lastResult.failed.length - 5} more.</li>
          )}
        </ul>
      )}
    </div>
  );
}
